import classNames from 'classnames';
import type { EditorConfig, LexicalEditor, LexicalNode, SerializedTextNode, Spread } from 'lexical';
import { TextNode } from 'lexical';

export enum SceneNumberNodeActiveState {
    DEFAULT = 'default',
    ICON = 'icon',
    BLURRED = 'blurred',
}

const SceneNumberNodeLexicalType = 'scene-number-node'; // DO NOT CHANGE THIS OR ELSE OLD TRANSCRIPTS WILL NOT RENDER
export type SerializedSceneNumberNode = Spread<
    {
        type: typeof SceneNumberNodeLexicalType;
        activeState?: SceneNumberNodeActiveState;
    },
    SerializedTextNode
>;
export class SceneNumberNode extends TextNode {
    __activeState?: SceneNumberNodeActiveState;

    constructor(text: string, key?: string, activeState?: SceneNumberNodeActiveState) {
        super(text, key);
        this.__activeState = activeState || SceneNumberNodeActiveState.DEFAULT;
    }

    static override getType() {
        return SceneNumberNodeLexicalType;
    }

    static override clone(node: SceneNumberNode): SceneNumberNode {
        return new SceneNumberNode(node.__text, node.__key, node.__activeState);
    }

    override createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
        const sceneNumber = document.createElement('span');
        sceneNumber.className = classNames('scene-number', {
            'material-symbols-outlined': this.__activeState === SceneNumberNodeActiveState.ICON,
            blurred: this.__activeState === SceneNumberNodeActiveState.BLURRED,
        });
        sceneNumber.contentEditable = 'false'; // do not allow the user to edit the scene number
        sceneNumber.textContent = this.__text;
        return sceneNumber;
    }

    override updateDOM(prevNode: SceneNumberNode, dom: HTMLElement, config: EditorConfig): boolean {
        super.updateDOM(prevNode as this, dom, config);
        dom.className = classNames('scene-number', {
            'material-symbols-outlined': this.__activeState === SceneNumberNodeActiveState.ICON,
            blurred: this.__activeState === SceneNumberNodeActiveState.BLURRED,
        });
        return false;
    }

    override exportJSON(): SerializedSceneNumberNode {
        return {
            ...super.exportJSON(),
            type: SceneNumberNodeLexicalType,
            activeState: this.activeState,
        };
    }

    static override importJSON(serializedNode: SerializedSceneNumberNode): SceneNumberNode {
        const node = $createSceneNumberNode(serializedNode.text);
        node.setActiveState(serializedNode.activeState || SceneNumberNodeActiveState.DEFAULT);
        return node;
    }

    get activeState() {
        return this.__activeState || SceneNumberNodeActiveState.DEFAULT;
    }

    setActiveState(state: SceneNumberNodeActiveState) {
        const self = this.getWritable();
        self.__activeState = state;
        return self;
    }

    override canInsertTextBefore(): boolean {
        // if the cursor is right next to the scene number node,
        // and the user begins typing, text should not go into the
        // scene number node
        return false;
    }

    override canInsertTextAfter(): boolean {
        // if the cursor is right next to the scene number node,
        // and the user begins typing, text should not go into the
        // scene number node
        return false;
    }
}

export const $createSceneNumberNode = (sceneNumber: string): SceneNumberNode => {
    return new SceneNumberNode(sceneNumber);
};

export const $isSceneNumberNode = (node: LexicalNode): node is SceneNumberNode => {
    return node instanceof SceneNumberNode;
};
